// @flow

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Switch from 'react-bootstrap-switch';
import I18n from 'PMT/i18n/I18n';
import cloneDeepWith from 'lodash/cloneDeepWith';

import ApiManagerOptions from 'constants/ApiManagerOptions';

// -- constants
import ApiConstants from 'PMT/constants/ApiConstants';
import TaxesType from 'PMT/constants/TaxesType';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import RestaurantMultipart from 'PMT/entities/RestaurantMultipart';
import RestaurantLegalBank from 'PMT/entities/RestaurantLegalBank';

import { CurrencyUtils } from 'PMT/utils';
import SecurityContext from 'utils/permissions/SecurityContext';

// -- views
import RestaurantFormComponent from './RestaurantFormComponent';
import PdfViewerModal from 'PMT/components/pdfViewer/PdfViewerModal';
import {
  TextInput,
  TransferFrequencyChooser,
  NumberInput,
  PdfDropzone,
  LocalizableSelect,
} from 'PMT/components/form';
import PayGreenConfigurationView from './PayGreenConfigurationView';
import SaferpayConfigurationView from './SaferpayConfigurationView';
import Config from 'constants/Config';

export default class PrivateFormView extends RestaurantFormComponent {
  static propTypes = {
    restaurant: PropTypes.objectOf(Restaurant),

    /**
     * @param RestaurantMultipart
     */
    onUpdate: PropTypes.func.isRequired,
  };

  state = this.getDefaultState(this.props.restaurant);

  //
  // RestaurantFormComponent override.
  //

  getDefaultState(restaurant: Restaurant) {
    return {
      acquirer: restaurant.acquirer,
      psp: restaurant.psp,
      pspSettings: restaurant.pspSettings
        ? cloneDeepWith(restaurant.pspSettings)
        : {},
      allowTips: restaurant.allowTips,

      bank: restaurant.legal.bank.copy(),
      legalBankRibFile: null,

      proofOfIncomeFile: null,

      // UI state
      displayRibPdfModal: false,
      displayProofOfIncomePdfModal: false,

      // form's warnings
      formWarnings: {},
    };
  }

  haveModifications() {
    const restaurant = this.props.restaurant;
    const state = this.state;

    return !(
      restaurant.acquirer === state.acquirer &&
      state.allowTips === restaurant.allowTips &&
      restaurant.psp === state.psp &&
      _.isEqual(restaurant.pspSettings, state.pspSettings) &&
      _.isNull(state.legalBankRibFile) &&
      _.isNull(state.proofOfIncomeFile) &&
      state.bank.equals(this.props.restaurant.legal.bank)
    );
  }

  isValid() {
    if (this.state.psp === 'MANGOPAY') {
      return (
        this.isBicValid(this.state.bank.bic) &&
        this.isIbanValid(this.state.bank.iban)
      );
    }
    return true; // consider it as valid to allow adding values that we have
    // https://paymytable.atlassian.net/browse/PP-1083
    //: !_.isEmpty(this.state.bank.pspRestaurantId) &&
    //    (!_.isEmpty(this.state.pspSettings) || !_.isEmpty(this.props.restaurant.pspSettings));
  }

  isBicValid(bic: string): boolean {
    const bicRegex = /^[a-zA-Z]{6}\w{2}(\w{3})?$/;
    return _.isEmpty(bic) || this.checkRegex(bicRegex, bic);
  }

  isIbanValid(iban: string): boolean {
    const ibanRegex = /[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}/;
    return _.isEmpty(iban) || this.checkRegex(ibanRegex, iban);
  }

  shouldDisableSubmit(): boolean {
    return !this.isValid();
  }

  //
  //
  //

  onShowRibPdfModal = () => {
    this.setState({
      displayRibPdfModal: true,
    });
  };

  onHideRibPdfModal = () => {
    this.setState({
      displayRibPdfModal: false,
    });
  };

  //
  //
  //

  onShowProofOfIncomePdfModal = () => {
    this.setState({
      displayProofOfIncomePdfModal: true,
    });
  };

  onHideProofOfIncomePdfModal = () => {
    this.setState({
      displayProofOfIncomePdfModal: false,
    });
  };

  //
  // form actions
  //

  onLegalBankBicChange = (bic: string) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.bic = bic;

    this.setState({
      bank,
      formWarnings: {
        ...this.state.formWarnings,
        bic: this.isBicValid(bic)
          ? ''
          : I18n.tr('restaurant_edit.finance.bic.invalid'),
      },
    });
  };

  onLegalBankIbanChange = (iban: string) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.iban = iban;
    this.setState({
      bank,
      formWarnings: {
        ...this.state.formWarnings,
        iban: this.isIbanValid(iban)
          ? ''
          : I18n.tr('restaurant_edit.finance.iban.invalid'),
      },
    });
  };

  onLegalFinancialDepartmentEmailChange = (
    financialDepartmentEmail: string
  ) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.financialDepartmentEmail = financialDepartmentEmail;
    this.setState({
      bank,
    });
  };

  onLegalBankTransferFrequencyChange = (transferFrequency: string) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.transferFrequency = transferFrequency;
    this.setState({
      bank,
    });
  };

  onLegalBankRibFileChange = (file) => {
    this.setState({
      legalBankRibFile: file,
    });
  };

  onProofOfIncomeFileChange = (file) => {
    this.setState({
      proofOfIncomeFile: file,
    });
  };

  onPspRestaurantIdChange = (pspRestaurantId) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.pspRestaurantId = pspRestaurantId;
    this.setState({
      bank,
    });
  };

  handleBankChoice = (psp) => {
    const acquirer = psp;

    const isSameAsInit = psp === this.props.restaurant.psp;

    this.setState({
      acquirer,
      psp,
      bank: isSameAsInit
        ? this.props.restaurant.legal.bank.copy()
        : new RestaurantLegalBank(),
      pspSettings: {},
    });
  };

  handlePspSettingsChange = (pspSettings) => {
    this.setState({
      pspSettings,
    });
  };

  //
  // Fee
  //

  onFeeFixedChange = (feeFixed) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.feeFixed = feeFixed;
    this.setState({
      bank,
    });
  };

  onFeeVariableChange = (feeVariable) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.feeVariable = feeVariable;
    this.setState({
      bank,
    });
  };

  onFeeVatPercentageChange = ({ value }) => {
    const bank: RestaurantLegalBank = this.state.bank;
    bank.feeVatPercentage = value;
    this.setState({
      bank,
    });
  };

  onAllowTipsChange = () => {
    this.setState({
      allowTips: !this.state.allowTips,
    });
  };

  onSubmit = (event) => {
    event.preventDefault();

    if (!this.isValid()) {
      return;
    }

    const restaurantMultipart: RestaurantMultipart = new RestaurantMultipart();

    restaurantMultipart.acquirer = this.state.acquirer;
    restaurantMultipart.psp = this.state.psp;
    restaurantMultipart.pspSettings = this.state.pspSettings;
    restaurantMultipart.allowTips = this.state.allowTips;

    restaurantMultipart.legal = this.props.restaurant.legal.copy();
    restaurantMultipart.legal.bank = this.state.bank.copy();

    if (!_.isNull(this.state.menuImage)) {
      restaurantMultipart.legalBankIdentityDocument = this.state.legalBankRibFile;
    }

    if (!_.isNull(this.state.menuImage)) {
      restaurantMultipart.proofOfIncome = this.state.proofOfIncomeFile;
    }

    this.props.onUpdate(restaurantMultipart);
  };

  //
  // renderers
  //

  /**
   * TODO: multiple select
   */
  renderInAcceptedCreditCard() {
    const acceptedCreditCards = [];

    if (this.restaurant.isAcceptingVisa()) {
      acceptedCreditCards.push('Visa');
    }
    if (this.restaurant.isAcceptingAmex()) {
      acceptedCreditCards.push('American express');
    }
    if (this.restaurant.isAcceptingMastercard()) {
      acceptedCreditCards.push('Mastercard');
    }

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.finance.accepted_payment_means')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="col-xs-6 input-group fake-input-group">
            <div className="form-control">{acceptedCreditCards.join(', ')}</div>
          </div>
        </div>
      </div>
    );
  }

  renderBic() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.finance.bic')}

            {this.renderBankIcon()}
          </label>
        </div>

        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.bank.bic}
            onChange={this.onLegalBankBicChange}
            readOnly={
              !SecurityContext.restaurantSettings.editFinanceBankAccount()
            }
          />
          <p
            className={`alert alert-danger top-buffer-10${
              _.isEmpty(this.state.formWarnings.bic) ? ' hide' : ''
            }`}
          >
            {this.state.formWarnings.bic}
          </p>
        </div>
      </div>
    );
  }

  renderCurrency() {
    const value = `${CurrencyUtils.getNameForCode(
      this.props.restaurant.currency
    )} (${this.props.restaurant.currency})`;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.finance.money')}</label>
        </div>

        <div className="col-xs-6">
          <TextInput readOnly value={value} />
        </div>
      </div>
    );
  }

  renderIban() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.finance.iban')}

            {this.renderBankIcon()}
          </label>
        </div>

        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.bank.iban}
            onChange={this.onLegalBankIbanChange}
            readOnly={
              !SecurityContext.restaurantSettings.editFinanceBankAccount()
            }
          />
          <p
            className={`alert alert-danger top-buffer-10${
              _.isEmpty(this.state.formWarnings.iban) ? ' hide' : ''
            }`}
          >
            {this.state.formWarnings.iban}
          </p>
        </div>
      </div>
    );
  }

  renderRibPdfModal() {
    const ribUrl = this.props.restaurant.legal.bank.identityDocument;

    const httpHeaders = ApiManagerOptions.headersMiddleware();

    return (
      <PdfViewerModal
        show={this.state.displayRibPdfModal}
        onHide={this.onHideRibPdfModal}
        file={ribUrl}
        httpHeaders={httpHeaders}
        title={I18n.tr('restaurant_edit.finance.rib')}
      />
    );
  }

  renderProofOfIncomePdfModal() {
    const url = this.props.restaurant.legal.proofOfIncome;

    const httpHeaders = ApiManagerOptions.headersMiddleware();

    return (
      <PdfViewerModal
        show={this.state.displayProofOfIncomePdfModal}
        onHide={this.onHideProofOfIncomePdfModal}
        file={url}
        httpHeaders={httpHeaders}
        title={'Proof of income'}
      />
    );
  }

  renderRib() {
    const ribUrl = this.props.restaurant.legal.bank.identityDocument;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.finance.rib')}</label>
        </div>

        <div className="col-xs-6">
          <PdfDropzone
            onChange={this.onLegalBankRibFileChange}
            file={this.state.legalBankRibFile}
            readOnly={
              !SecurityContext.restaurantSettings.editFinanceBankAccount()
            }
          />
          <br />
          {/* TODO: popin pdf */}
          {!_.isEmpty(ribUrl) && (
            <a onClick={this.onShowRibPdfModal}>
              {I18n.tr('restaurant_edit.finance.rib.see_file')}
            </a>
          )}
        </div>
      </div>
    );
  }

  renderBankTransferFrequency() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.finance.automatic_payout_frequency')}
          </label>
        </div>

        <div className="col-xs-6">
          <TransferFrequencyChooser
            value={this.state.bank.transferFrequency}
            onChange={this.onLegalBankTransferFrequencyChange}
            readOnly={!SecurityContext.restaurantSettings.editFinanceOther()}
          />
        </div>
      </div>
    );
  }

  renderFinancialDepartmentEmail() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.finance.financial_department_email')}
          </label>
        </div>

        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.bank.financialDepartmentEmail}
            onChange={this.onLegalFinancialDepartmentEmailChange}
            readOnly={!SecurityContext.restaurantSettings.editFinanceOther()}
          />
        </div>
      </div>
    );
  }

  rendersBankChoice() {
    return (
      <div>
        <div className="row form-group">
          <div className="col-xs-4 text-right">
            <label>
              {I18n.tr('restaurant_edit.configuration.bank.choice')}
            </label>
          </div>
          <div className="col-xs-6">
            <div
              onClick={() => this.handleBankChoice('MANGOPAY')}
              style={{
                padding: 20,
                background: this.state.psp === 'MANGOPAY' ? 'red' : '',
              }}
            >
              {/*I18n.tr('restaurant_edit.configuration.bank.choice.default')*/}
              Mangopay
            </div>
            {/* <div onClick={() => this.handleBankChoice('PAYZEN')}>
              {I18n.tr('restaurant_edit.configuration.bank.choice.payzen')}
            </div> */}
            <div
              onClick={() => this.handleBankChoice('PAYGREEN')}
              style={{
                padding: 20,
                background: this.state.psp === 'PAYGREEN' ? 'red' : '',
              }}
            >
              PayGreen
            </div>

            <div
                onClick={() => this.handleBankChoice('SAFERPAY')}
                style={{
                  padding: 20,
                  background: this.state.psp === 'SAFERPAY' ? 'red' : '',
                }}
            >
              Saferpay
            </div>

            <div
                onClick={() => this.handleBankChoice('ADYEN')}
                style={{
                  padding: 20,
                  background: this.state.psp === 'ADYEN' ? 'red' : '',
                }}
            >
              Adyen
            </div>

            {SecurityContext.admin.isAdmin() && (
                <div
                    onClick={() => this.handleBankChoice('FAKE')}
                    style={{
                      padding: 20,
                      background: this.state.psp === 'FAKE' ? 'red' : '',
                    }}
                >
                  Fake PSP
              </div>
            )}
          </div>
        </div>

        {this.renderPspRestaurantId()}
        {this.renderAcquirerWalletId()}
        {this.renderAcquirerBankId()}
      </div>
    );
  }

  renderPspRestaurantId() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.configuration.bank.psp.restaurantId')}
          </label>
        </div>

        <div className="col-xs-6">
          <TextInput
            value={this.state.bank.pspRestaurantId}
            onChange={this.onPspRestaurantIdChange}
          />
        </div>
      </div>
    );
  }

  renderAcquirerWalletId() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.configuration.bank.acquirer.wallet_id')}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput value={this.state.bank.acquirerWalletId} />
        </div>
      </div>
    );
  }

  renderAcquirerBankId() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.configuration.bank.acquirer.bank_id')}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput value={this.state.bank.acquirerBankAccountId} />
        </div>
      </div>
    );
  }

  //
  // Fee
  //

  renderFeeFixed() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr(
              'restaurant_edit.configuration.commission_per_transaction'
            )}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <NumberInput
              value={this.state.bank.feeFixed}
              onChange={this.onFeeFixedChange}
              min={0}
              readOnly={
                !SecurityContext.restaurantSettings.editConfigurationCommission()
              }
            />
            <div className="input-group-addon">
              <label>{CurrencyUtils.getCurrencySymbol(
                  this.props.restaurant.currency)}</label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFeeVariable() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr(
              'restaurant_edit.configuration.commission_per_transaction_variable'
            )}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <NumberInput
              value={this.state.bank.feeVariable}
              onChange={this.onFeeVariableChange}
              readOnly={
                !SecurityContext.restaurantSettings.editConfigurationCommission()
              }
            />
            <div className="input-group-addon">
              <label>%</label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFeeVatPercentage() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 text-right">
          <label>
            {I18n.tr('restaurant_edit.configuration.fee_vat_percentage')}
          </label>
        </div>

        <div className="col-xs-6">
          <LocalizableSelect
            clearable={false}
            value={this.state.bank.feeVatPercentage}
            options={TaxesType.getLegalBankFeeTaxes()}
            onChange={this.onFeeVatPercentageChange}
            readOnly={
              !SecurityContext.restaurantSettings.editConfigurationCommission()
            }
          />
        </div>
      </div>
    );
  }

  renderAllowTips() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 text-right">
          <label>{I18n.tr('restaurant_edit.configuration.tips')}</label>
        </div>

        <div className="col-xs-6">
          <Switch
            bsSize="mini"
            // defaultValue={this.props.retaurant.allowTips}
            value={this.state.allowTips}
            onChange={this.onAllowTipsChange}
            disabled={
              !SecurityContext.restaurantSettings.editConfigurationCommission()
            }
          />
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="edit_restaurant_form_view__private">
        <form className="form-horizontal">
          {this.renderIconsLegend()}

          {SecurityContext.restaurantSettings.readConfigurationMangoPay() && (
              <div className="top-buffer-50">
                <h3 className="text-center">
                  {I18n.tr('restaurant_edit.configuration.bank')}
                </h3>

                <div className="top-buffer-50">{this.rendersBankChoice()}</div>
                <hr className="top-buffer-50"/>
              </div>
          )}

          <div className="top-buffer-50">
            {this.renderCurrency()}
            <hr className="top-buffer-50"/>
          </div>

          {(this.state.psp === 'MANGOPAY' || this.state.psp === 'PAYGREEN') && (
              <div>
                {SecurityContext.restaurantSettings.readFinanceBankAccount()
                    && (
                        <div>
                          <h3 className="text-center">
                            {I18n.tr('restaurant_edit.finance.bank_account')}
                          </h3>

                          <div className="top-buffer-50">
                            {this.renderIban()}
                            {this.renderRib()}
                            {this.renderBic()}
                          </div>

                          <hr className="top-buffer-50"/>
                        </div>
                    )}

                {SecurityContext.restaurantSettings.readFinanceOther() && (
                    <div className="top-buffer-50">
                      <h3 className="text-center">
                        {I18n.tr('restaurant_edit.finance.other')}
                      </h3>

                      <div className="top-buffer-50">
                        {this.renderInAcceptedCreditCard()}
                        {this.renderBankTransferFrequency()}
                        {this.renderFinancialDepartmentEmail()}
                      </div>

                      <hr className="top-buffer-50"/>
                    </div>
                )}

                {SecurityContext.restaurantSettings.readConfigurationCommission()
                    && (
                        <div className="top-buffer-50">
                          <h3 className="text-center">
                            {I18n.tr(
                                'restaurant_edit.configuration.commissions')}
                          </h3>

                          <div className="top-buffer-50">
                            {this.renderFeeFixed()}
                            {this.renderFeeVariable()}
                            {this.renderFeeVatPercentage()}
                            {this.renderAllowTips()}
                          </div>
                          <hr className="top-buffer-50"/>
                        </div>
                    )}
              </div>
          )}

          {this.state.psp === 'PAYGREEN' && (
              <PayGreenConfigurationView
                  onChange={(pspSettings) => this.setState({pspSettings})}
                  pspSettings={this.state.pspSettings}
                  bank={this.state.bank}
                  legal={this.props.restaurant.legal}
                  onChangeBank={(bank) => this.setState({bank})}
                  restaurant={this.props.restaurant}
              />
          )}

          {this.state.psp === 'SAFERPAY' && (
              <SaferpayConfigurationView
                  onChange={(pspSettings) => this.setState({pspSettings})}
                  pspSettings={this.state.pspSettings}
              />
          )}

          {/* PayGreen specific file to upload */}
          {this.state.psp === 'PAYGREEN' && (
              <div className="row form-group">
                <div className="col-xs-4 control-label">
                  <label>Proof of income</label>
                </div>

                <div className="col-xs-6">
                  <PdfDropzone
                      onChange={this.onProofOfIncomeFileChange}
                      file={this.state.proofOfIncomeFile}
                      readOnly={
                        !SecurityContext.restaurantSettings.editFinanceBankAccount()
                      }
                  />

                  <br/>
                  {!_.isEmpty(
                      _.get(this.props.restaurant, 'legal.proofOfIncome', null)
                  ) && (
                      <a onClick={this.onShowProofOfIncomePdfModal}>
                        {I18n.tr('restaurant_edit.finance.rib.see_file')}
                      </a>
                  )}
                </div>
              </div>
          )}

          {this.state.psp === 'ADYEN' && (
              <div className="row form-group text-center">
                  <span>
                  {I18n.tr(
                      'restaurant_edit.finance.settings_on_restaurantsgroup')}
                    <a href={Config.BOV2_URL}
                       target={"_blank"}>{Config.BOV2_URL}</a>
                </span>
              </div>
          )}

          {this.renderButtons()}

          {this.renderRibPdfModal()}
          {this.renderProofOfIncomePdfModal()}
        </form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/pages/restaurant/edit/components/FinanceFormView.js